/**
 * SessionList - Collapsible list of all sessions
 *
 * Features:
 * - Shows all sessions in a compact list
 * - Collapse/expand toggle (persisted to localStorage)
 * - Monitor button for non-monitored sessions
 * - Sort options: by name (A-Z) or by last activity (recent first)
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import { Card, Group, Text, Badge, ActionIcon, Collapse, Stack, SegmentedControl, Box } from '@mantine/core'
import { IconChevronDown, IconChevronRight, IconList, IconSortAscending, IconClock } from '@tabler/icons-react'
import { SessionListItem } from './SessionListItem'
import type { SessionInfo } from '../hooks/useSessionMonitor'

const STORAGE_KEY = 'logsession-monitor-list-collapsed'
const SORT_STORAGE_KEY = 'logsession-monitor-list-sort'

type SortOption = 'name' | 'activity'

interface SessionListProps {
  sessions: SessionInfo[]
  monitoredSessions: string[]
  onMonitor: (name: string) => void
}

export function SessionList({ sessions, monitoredSessions, onMonitor }: SessionListProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored === 'true'
    } catch {
      return false
    }
  })

  const [sortBy, setSortBy] = useState<SortOption>(() => {
    try {
      const stored = localStorage.getItem(SORT_STORAGE_KEY)
      return (stored === 'name' || stored === 'activity') ? stored : 'name'
    } catch {
      return 'name'
    }
  })

  // Track last activity timestamps per session
  // Key: session name, Value: { timestamp, status }
  const activityMapRef = useRef<Map<string, { timestamp: number; status: string }>>(new Map())

  // Update activity timestamps when sessions change
  useEffect(() => {
    const now = Date.now()
    sessions.forEach(session => {
      const existing = activityMapRef.current.get(session.name)
      // Update timestamp if: new session, or status changed
      if (!existing || existing.status !== session.status) {
        activityMapRef.current.set(session.name, {
          timestamp: now,
          status: session.status
        })
      }
    })
  }, [sessions])

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed))
  }, [collapsed])

  // Persist sort option
  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sortBy)
  }, [sortBy])

  const isMonitored = (name: string) => monitoredSessions.includes(name)

  // Status counts
  const statusCounts = {
    total: sessions.length,
    monitored: monitoredSessions.filter(name => sessions.some(s => s.name === name)).length,
    waiting: sessions.filter(s => s.status === 'waiting').length,
    active: sessions.filter(s => s.status === 'active').length,
  }

  // Sort sessions
  const sortedSessions = useMemo(() => {
    const sorted = [...sessions]
    if (sortBy === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name))
    } else {
      // Sort by last activity (most recent first)
      sorted.sort((a, b) => {
        const aActivity = activityMapRef.current.get(a.name)?.timestamp ?? 0
        const bActivity = activityMapRef.current.get(b.name)?.timestamp ?? 0
        return bActivity - aActivity
      })
    }
    return sorted
  }, [sessions, sortBy])

  return (
    <Card withBorder p="sm" mb="md">
      {/* Header */}
      <Group
        justify="space-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setCollapsed(!collapsed)}
      >
        <Group gap="sm">
          <ActionIcon variant="subtle" color="gray" size="sm">
            {collapsed ? <IconChevronRight size={18} /> : <IconChevronDown size={18} />}
          </ActionIcon>
          <IconList size={20} />
          <Text fw={600}>All Sessions</Text>
        </Group>

        <Group gap="xs">
          {statusCounts.waiting > 0 && (
            <Badge size="sm" color="yellow" variant="filled">
              {statusCounts.waiting} waiting
            </Badge>
          )}
          {statusCounts.active > 0 && (
            <Badge size="sm" color="blue" variant="light">
              {statusCounts.active} active
            </Badge>
          )}
          <Badge size="sm" color="gray" variant="light">
            {statusCounts.total} sessions
          </Badge>
          {statusCounts.monitored > 0 && (
            <Badge size="sm" color="violet" variant="light">
              {statusCounts.monitored} monitored
            </Badge>
          )}
        </Group>
      </Group>

      {/* Collapsible content */}
      <Collapse in={!collapsed}>
        {/* Sort options */}
        <Box mt="sm" mb="xs" px="sm">
          <SegmentedControl
            size="xs"
            value={sortBy}
            onChange={(value) => setSortBy(value as SortOption)}
            data={[
              {
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconSortAscending size={14} />
                    <span>Name</span>
                  </Group>
                ),
                value: 'name'
              },
              {
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconClock size={14} />
                    <span>Recent Activity</span>
                  </Group>
                ),
                value: 'activity'
              }
            ]}
          />
        </Box>

        <Stack gap={0}>
          {sortedSessions.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="md">
              No active sessions. Start a loggable-session to see it here.
            </Text>
          ) : (
            sortedSessions.map((session) => (
              <SessionListItem
                key={session.name}
                session={session}
                isMonitored={isMonitored(session.name)}
                onMonitor={() => onMonitor(session.name)}
              />
            ))
          )}
        </Stack>
      </Collapse>
    </Card>
  )
}
